import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  Plus,
  Loader2,
  CheckCircle,
  User,
  File,
  Download,
} from 'lucide-react';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import {
  usePatientMessages,
  useMarkMessageRead,
} from '@/hooks/usePatientMessages';
import { useMessageAttachments } from '@/hooks/useMessageAttachments';
import { useGetDocumentDownloadUrl } from '@/hooks/useDocuments';
import { usePatient } from '@/hooks/usePatients';
import { Button } from '@/components/ui/Button';
import { MessageModal } from '@/pages/appointments/MessageModal';

interface MessageTabProps {
  patientId: string;
  clinicId: string;
}

function MessageAttachments({ messageId }: { messageId: string }) {
  const { t } = useTranslation();
  const { data: attachments, isLoading } = useMessageAttachments(messageId);
  const getDownloadUrl = useGetDocumentDownloadUrl();

  const handleDownload = async (documentId: string, filename: string) => {
    try {
      const url = await getDownloadUrl.mutateAsync(documentId);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      toast.error(t('documents.errors.download_failed', 'Kunne ikke downloade fil'));
    }
  };

  if (isLoading || !attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 space-y-1">
      {attachments.map((attachment) => (
        <button
          key={attachment.id}
          onClick={() => handleDownload(attachment.document_id, attachment.document.original_filename)}
          disabled={getDownloadUrl.isPending}
          className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          <File className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{attachment.document.original_filename}</span>
          <Download className="h-3 w-3 flex-shrink-0 ml-auto" />
        </button>
      ))}
    </div>
  );
}

export function MessageTab({ patientId, clinicId }: MessageTabProps) {
  const { t } = useTranslation();
  const { data: messages, isLoading, error } = usePatientMessages(patientId);
  const { data: patient } = usePatient(patientId);
  const markRead = useMarkMessageRead();
  
  const [showModal, setShowModal] = useState(false);

  const handleMarkAsRead = async (messageId: string) => {
    try {
      await markRead.mutateAsync({
        message_id: messageId,
        patient_id: patientId,
      });
      toast.success(t('messages.marked_read'));
    } catch (err) {
      console.error('Error marking message as read:', err);
      toast.error(t('messages.error_marking_read'));
    }
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return format(date, 'dd. MMMM yyyy HH:mm', { locale: da });
  };

  const renderMessage = (msg: NonNullable<typeof messages>[number]) => {
    const isFromPatient = msg.sender === 'patient';
    const isUnread = !msg.read_at && isFromPatient;

    return (
      <div
        key={msg.id}
        className={`rounded-lg border p-4 ${
          isUnread
            ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-900/20'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-card-dark'
        }`}
      >
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                isFromPatient
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              }`}
            >
              {isFromPatient ? (
                <User className="h-4 w-4" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {isFromPatient ? t('messages.from_patient') : msg.sender_name ?? t('messages.from_clinic')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatDateTime(msg.created_at)}
              </p>
            </div>
          </div>
          {isUnread && (
            <button
              onClick={() => handleMarkAsRead(msg.id)}
              disabled={markRead.isPending}
              className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              <CheckCircle className="h-3 w-3" />
              {t('messages.mark_read')}
            </button>
          )}
        </div>
        <div className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
          {msg.content}
        </div>
        <MessageAttachments messageId={msg.id} />
        {msg.read_at && !isFromPatient && (
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            {t('messages.read_at')} {formatDateTime(msg.read_at)}
          </p>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-200">
        {t('messages.error_loading')}
      </div>
    );
  }

  const patientName = patient 
    ? `${patient.first_name} ${patient.last_name}`
    : 'Patient';

  return (
    <div className="space-y-6">
      {showModal && patient && (
        <MessageModal
          patientId={patientId}
          patientName={patientName}
          clinicId={clinicId}
          onClose={() => setShowModal(false)}
        />
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {t('messages.title')}
        </h3>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('messages.new_message')}
        </Button>
      </div>

      {!messages || messages.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('messages.no_messages')}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            {t('messages.no_messages_hint')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map(renderMessage)}
        </div>
      )}
    </div>
  );
}
